// Types for the 다운로드 centre (자료실) — downloadable desktop applications and their installer
// releases. Mirrors the BFF JSON contracts under /api/downloads and /api/admin/downloads.
//
// This is NOT the SDUI app marketplace: a desktop app is a binary the user installs on their own
// machine, every signed-in user may download one, and only a platform admin may publish.

export type DesktopPlatform = "WINDOWS" | "MACOS" | "LINUX" | "ANY";

export const DESKTOP_PLATFORMS: DesktopPlatform[] = ["WINDOWS", "MACOS", "LINUX", "ANY"];

export const PLATFORM_LABEL: Record<DesktopPlatform, string> = {
  WINDOWS: "Windows",
  MACOS: "macOS",
  LINUX: "Linux",
  ANY: "공용",
};

export interface DesktopRelease {
  id: string;
  appId: string;
  version: string;
  platform: DesktopPlatform;
  filename: string;
  contentType: string;
  size: number;
  releaseNotes: string;
  downloadCount: number;
  uploadedBy: string | null;
  releasedAt: string;
}

export interface DesktopApp {
  id: string;
  slug: string;
  name: string;
  description: string;
  icon: string | null;
  hasIconImage: boolean;
  published: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Newest release first — releases[0] is the current build. */
  releases: DesktopRelease[];
}

export interface RegisterAppRequest {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  /** Omit to keep the server default (published). The UI registers unpublished on purpose. */
  published?: boolean;
}

export interface UpdateAppRequest {
  name?: string;
  description?: string;
  icon?: string;
  published?: boolean;
}

/** Human-readable file size. Installers are big, so MB/GB matter more than bytes. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * The build a user should get: the newest release for their OS, falling back to a cross-platform
 * one. Releases arrive newest-first, so the first match is the current build.
 */
export function latestFor(app: DesktopApp, platform: DesktopPlatform): DesktopRelease | null {
  return app.releases.find((r) => r.platform === platform)
    ?? app.releases.find((r) => r.platform === "ANY")
    ?? null;
}

/** Best guess at the visitor's OS, used to pre-select the download button. */
export function detectPlatform(): DesktopPlatform {
  const ua = navigator.userAgent;
  if (/Win/i.test(ua)) return "WINDOWS";
  if (/Mac/i.test(ua)) return "MACOS";
  if (/Linux|X11/i.test(ua)) return "LINUX";
  return "ANY";
}
